import React from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { useTheme } from '@/hooks/useTheme'
import { BaseView } from '@/components/base'
import { AnimatedCryptoInput } from '../AnimatedCryptoInput'
import { AppIcons } from '@/components/AppIcons'
import {
  useCryptoConversion,
  useInputsController,
  useSwapAnimation,
} from '../../hooks'
import { useConversionScopedStore } from '@/store/scoped'
import { MUTATION_KEYS } from '@/constants/mutationKeys'

export interface CryptoSwapInputsProps {
  style?: React.CSSProperties
}

export const CryptoSwapInputs: React.FC<CryptoSwapInputsProps> = ({
  style,
}) => {
  const theme = useTheme()
  const {
    handleInputChange,
    handleSwapCurrencies,
    isFiatMode,
    handleInputChangeInFiatMode,
  } = useCryptoConversion()
  const { triggerSwapAnimation } = useSwapAnimation({
    executeMidpointFunction: handleSwapCurrencies,
  })
  const { debitInputRef, creditInputRef, onFocus } = useInputsController()

  const { from, to } = useConversionScopedStore((state) => state.conversion)

  const isCalculatingFee =
    useIsMutating({
      mutationKey: [MUTATION_KEYS.CALCULATE_CONVERSION_FEE],
    }) > 0

  const debitValue = isFiatMode
    ? from.fiatAmount?.amount
    : from.cryptoAmount?.amount
  const creditValue = isFiatMode
    ? to.fiatAmount?.amount
    : to.cryptoAmount?.amount

  const debitEquivalentValue = isFiatMode ? from.cryptoAmount : from.fiatAmount
  const creditEquivalentValue = isFiatMode ? to.cryptoAmount : to.fiatAmount

  return (
    <BaseView
      data-component="crypto-swap-inputs-container"
      align="center"
      gap={16}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      <AnimatedCryptoInput
        ref={debitInputRef}
        type="from"
        value={debitValue}
        onFocus={() => onFocus('debit')}
        isFiduciarySwitchable
        onChangeText={(value) => {
          if (isFiatMode) {
            return handleInputChangeInFiatMode({ type: 'from', value })
          }

          handleInputChange({ type: 'from', value })
        }}
        defaultValue="0"
        currencyInfoInWallet={from.currencyInfoInWallet}
        equivalentValue={debitEquivalentValue}
        maxLength={20}
      />

      <AnimatedCryptoInput
        ref={creditInputRef}
        type="to"
        value={creditValue}
        editable={false}
        defaultValue="0"
        currencyInfoInWallet={to.currencyInfoInWallet}
        equivalentValue={creditEquivalentValue}
        maxLength={20}
        isLoading={isCalculatingFee}
      />

      {/* Overlay to hide input underline during swap animation */}
      <BaseView
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 50,
          height: 50,
          borderRadius: 16,
          backgroundColor: '#444345',
          zIndex: 1,
        }}
      />

      {/* Swap button */}
      <BaseView
        onClick={triggerSwapAnimation}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 50,
          height: 50,
          borderRadius: 16,
          backgroundColor: '#444345',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <AppIcons name="switch" size={20} color={theme.colors.label.default} />
      </BaseView>
    </BaseView>
  )
}
